import React, { useState, useEffect } from "react";
import { Grid, withStyles } from "@material-ui/core";
import { getListValues } from "../../../../API/List";
import { AttributeGroup } from "../../../../Component";
import CheckBox from "../../../../Component/Forms/CheckboxWithLabel";
import { useTranslation } from "react-i18next";

import styles from "./styles";

const AssociationForm = (props) => {
  const {
    isEditing,
    targetEntityId,
    allowedAssociations,
    noAassociationError,
    canRemoveAssociation,
    associations,
    existingAssociations = [],
    setHasAttributeError,
    classes,
    defaultType,
  } = props;
  const { t } = useTranslation();

  const selected = allowedAssociations.reduce(function (ass, as) {
    ass[as.id] = false;
    return ass;
  }, {});

  const [selectedAssociations, setSelectedAssociations] = useState(selected);
  const [existingIds, setExistingIds] = useState([]);
  const [listOptions, setListOptions] = useState();
  const [perError, setPerError] = useState();

  useEffect(() => {
    const getListOptions = async (listId, listType) => {
      const listValues = [];
      try {
        const result = await getListValues({
          listId,
          listName: null,
          listType,
        });
        const { data = [] } = result;
        for (let lv of data) {
          if (listType === "RL" || listType === "EL") {
            listValues.push({
              value: lv.code,
              label: lv.value,
              equivalent: lv.equivalent,
            });
          } else {
            listValues.push({ value: lv.code, label: lv.value });
          }
        }
      } catch (e) {
        console.log("error in fetching list " + e);
      }
      return listValues;
    };

    const loadListOptions = async () => {
      const listsToLoad = [];
      allowedAssociations.forEach((as) => {
        as.attributes.map(async (a) => {
          if (
            a.list_id ||
            a.data_type === "CL" ||
            a.data_type === "RL" ||
            a.data_type === "EL"
          ) {
            const e = listsToLoad.find(
              (l) => l.listId === a.list_id && l.dataType === a.data_type,
            );
            if (!e) {
              listsToLoad.push({ listId: a.list_id, dataType: a.data_type });
            }
          }
          return a;
        });
      });
      const los = {};
      await Promise.all(
        listsToLoad.map(async (l) => {
          los[l.listId || l.dataType] = await getListOptions(
            l.listId,
            l.dataType,
          );
          return l;
        }),
      );
      setListOptions(los);
    };

    loadListOptions();

    const existingIds = [];
    const selected = allowedAssociations.reduce(function (ass, as) {
      const existing = associations.find((a) => a.associationId === as.id);
      ass[as.id] = existing !== undefined;
      if (existing) {
        existingIds.push(as.id);
      }
      if (defaultType) {
        ass[as.id] = defaultType === as.name;
        selectAssociation(as, ass[as.id]);
      }
      return ass;
    }, {});
    setSelectedAssociations(selected);
    setExistingIds(existingIds);
  }, [allowedAssociations, associations]);

  const addAssociation = (as, selected) => {
    if (selected) {
      const { id: associationId, name: associationName, attributes } = as;
      associations.push({
        associationId,
        associationName,
        attributes,
      });
    } else {
      let idx = associations.findIndex((a) => a.associationId === as.id);
      if (idx > -1) {
        associations.splice(idx, 1);
      }
    }
  };

  const getTotalOwnership = (newValue) => {
    let totalOwnershipPer = 0;
    for (let ea of existingAssociations) {
      const { associations = [], entity } = ea;
      if (isEditing && entity.id === targetEntityId) {
        totalOwnershipPer += newValue ? Number(newValue) : 0;
      } else {
        const as = associations.find((as) => as.associationId === "2");
        const { attributes = [] } = as || {};
        const perAttr = attributes.find((a) => a.id === "81");
        const perVal = perAttr ? perAttr.user_value : 0;
        totalOwnershipPer += perVal;
      }
    }
    if (!isEditing) {
      totalOwnershipPer += newValue ? Number(newValue) : 0;
    }
    return totalOwnershipPer;
  };

  const validateOwnershipPercentage = (newValue) => {
    if (newValue > 100) {
      setHasAttributeError(true);
      setPerError(true);
      return;
    }
    let total = getTotalOwnership(newValue);
    if (total > 100) {
      setHasAttributeError(true);
      setPerError(true);
      return;
    }
    setHasAttributeError(false);
    setPerError(false);
  };

  const setAttributeValue = (associationId, attributeId, value) => {
    if (associationId === "2" && attributeId === "81") {
      validateOwnershipPercentage(value);
    }

    const as = associations.find((as) => as.associationId === associationId);
    if (as) {
      const attribute = as.attributes.find((a) => a.id === attributeId);
      attribute.user_value = value;
    }
  };

  const selectAssociation = (as, selected) => {
    const sas = { ...selectedAssociations };
    sas[as.id] = selected;
    setSelectedAssociations(sas);
    addAssociation(as, selected);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={11}>
        {allowedAssociations.map((as) => {
          const existing = associations.find((a) => a.associationId === as.id);
          return (
            <Grid container spacing={1} key={as.id}>
              <Grid item xs={12}>
                <div className={classes.header}>
                  <CheckBox
                    label={as.name}
                    onChange={(e) => selectAssociation(as, e.target.checked)}
                    value={selectedAssociations[as.id]}
                    disabled={
                      !canRemoveAssociation && existingIds.indexOf(as.id) > -1
                    }
                  />
                </div>
              </Grid>
              {selectedAssociations[as.id] && (
                <Grid item sm={12}>
                  <AttributeGroup
                    attributes={existing ? existing.attributes : as.attributes}
                    checkType={"AML"}
                    onChange={(id, value) =>
                      setAttributeValue(as.id, id, value)
                    }
                    listOptions={listOptions}
                  />
                  <div className={classes.errorText}>
                    {perError &&
                      as.id === "2" &&
                      t(
                        "messages.record.associations.ownershipPercentageError",
                      )}
                  </div>
                </Grid>
              )}
            </Grid>
          );
        })}
      </Grid>
      <div className={classes.errorText}>
        {noAassociationError &&
          t("messages.record.associations.associationError")}
      </div>
    </Grid>
  );
};

export default withStyles(styles, { withTheme: true })(AssociationForm);
